import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  acceptedTerms,
  ForgotPassword,
  LoginBody,
  ProfileDetails,
  ResetPasswordPayload,
  verifyEmailOrPhone,
  VerifyOtp
} from '../models/authmodel';
import { changePasswordPayload } from '../models/changePasswordPayload';
import { PhoneFormat } from '../models/phone';
import { ISignUpPayload } from '../models/sign-up-payload.interface';
import { ILoginPlayload } from '../models/login-payload.interface';
import { IProfileDetails } from '../models/profile-details.intarface';
import { skipAccessInterceptor } from '../../config/tokens/bypass-access-interceptor.token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(private readonly http: HttpClient) {}
  private menuState = new BehaviorSubject<boolean>(false);
  menuState$ = this.menuState.asObservable();
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpenSubject.asObservable();
  FCMtoken: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
  resetTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  profileDetails: BehaviorSubject<ProfileDetails | null> =
    new BehaviorSubject<ProfileDetails | null>(null);
  emailSubject: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);
  resetPhoneSubject: BehaviorSubject<PhoneFormat | null> =
    new BehaviorSubject<PhoneFormat | null>(null);

  public get isGuest(): boolean {
    const tokenValue = localStorage.getItem(environment.storageKey);

    return !tokenValue || tokenValue === environment.storageKey;
  }

  loginUserByEmail(
    data: Partial<ILoginPlayload>
  ): Observable<Partial<IProfileDetails & { userId?: string }>> {
    return this.http
      .post<{
        data: Partial<ILoginPlayload>;
      }>(`${environment.base_url}login`, data, {
        context: skipAccessInterceptor()
      })
      .pipe(map(res => res.data));
  }

  loginUserByPhone(loginDetails: LoginBody): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}sendOtp`, loginDetails)
      .pipe(map(res => res.data));
  }

  deleteAccount(_id: IProfileDetails['_id']): Observable<any> {
    return this.http.delete<{
      data: ProfileDetails;
    }>(`${environment.base_url}deleteProfile`, { body: { user: { _id } } });
  }

  getProfileDetails(): Observable<ProfileDetails> {
    return this.http
      .get<{
        data: ProfileDetails;
      }>(`${environment.base_url}getProfile`)
      .pipe(map(res => res.data));
  }

  signUpUser(payload: Partial<ISignUpPayload>): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}signup`, payload, {
        context: skipAccessInterceptor()
      })
      .pipe(map(res => res.data));
  }

  validateCredentials(payload: Partial<ISignUpPayload>): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}validate-credentials`, payload, {
        context: skipAccessInterceptor()
      })
      .pipe(map(res => res.data));
  }

  // verifyOtp(payload: VerifyOtp): Observable<ProfileDetails> {
  //   return this.http
  //     .post<{
  //       data: ProfileDetails;
  //     }>(`${environment.base_url}verifyOtp`, payload)
  //     .pipe(map(res => res.data));
  // }

  verifyOtp(payload: Partial<VerifyOtp>): Observable<ProfileDetails> {
    // Get userId from localStorage
    const userId = localStorage.getItem('userId');

    // Construct the query string if userId is present
    const queryString = userId ? `?userId=${encodeURIComponent(userId)}` : '';

    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}verifyOtp${queryString}`, payload, {
        context: skipAccessInterceptor()
      })
      .pipe(map(res => res.data));
  }

  verifyForgotPass(payload: VerifyOtp): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}verifyForgotPass`, payload, {
        context: skipAccessInterceptor()
      })
      .pipe(map(res => res.data));
  }

  // resendOtp(payload: any): Observable<ProfileDetails> {
  //   return this.http
  //     .post<{
  //       data: ProfileDetails;
  //     }>(`${environment.base_url}sendOtp`, payload)
  //     .pipe(map(res => res.data));
  // }

  resendOtp(
    payload: any,
    type?: 'forgetPass' | null
  ): Observable<ProfileDetails> {
    // Get userId from localStorage
    const userId = localStorage.getItem('userId');

    let params = new HttpParams();

    const queries = {
      userId: userId ? encodeURIComponent(userId) : null,
      type
    } as const;

    Object.entries(queries)
      .filter(([_, v]) => Boolean(v))
      .forEach(([k, v]) => {
        if (!v) return;
        params = params.append(k, v.toString());
      });

    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}sendOtp`, payload, { params, context: skipAccessInterceptor() })
      .pipe(map(res => res.data));
  }

  updateProfile(
    payload: ProfileDetails | acceptedTerms
  ): Observable<ProfileDetails> {
    return this.http
      .put<{
        data: ProfileDetails;
      }>(`${environment.base_url}updateProfile`, payload)
      .pipe(map(res => res.data));
  }

  forgotPassword(payload: ForgotPassword): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}forgotPassword`, payload, { context: skipAccessInterceptor() })
      .pipe(map(res => res.data));
  }

  resetPassword(data: ResetPasswordPayload): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}resetPassword`, data)
      .pipe(map(res => res.data));
  }

  verifyEmailOrPhone(payload: verifyEmailOrPhone): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}validateAccount`, payload)
      .pipe(map(res => res.data));
  }

  verifyMailOrPhoneOtp(
    payload: verifyEmailOrPhone
  ): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}verifyValidate`, payload)
      .pipe(map(res => res.data));
  }

  changePassword(payload: changePasswordPayload): Observable<ProfileDetails> {
    return this.http
      .post<{
        data: ProfileDetails;
      }>(`${environment.base_url}changePassword`, payload)
      .pipe(map(res => res.data));
  }

  logout(): Observable<unknown> {
    return this.http.get<unknown>(`${environment.base_url}logout`);
  }

  toggleSidebarView(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  toggleMenu(): void {
    this.menuState.next(!this.menuState.value);
  }

  closeMenu(): void {
    this.menuState.next(false);
  }

  openMenu(): void {
    this.menuState.next(true);
  }
}
